/* eslint-disable import/no-unresolved */
/* eslint-disable import/extensions */
import _ from 'underscore';
import { uncheckedInjectionContext as globalContext } from '@bingads-webui-campaign/injection-context';
import { AuctionInsightService } from '@bingads-webui-campaign/auction-insight-service';
import { GENERAL_INSIGHTS_FEATURE_NAMES, CAMPAIGN_TYPES } from '@bingads-webui-campaign/competition-auction-insight-preferences';
import { GENERAL_INSIGHTS_DATA_TYPES, getFilterConfig, instrumentationSchema } from '@bingads-webui-campaign-react/competition-core';
import { FilterService } from '@bingads-webui/filter-service';

import { initializeDataSources, getDataSource } from '../general-insights-data-source';
import { GeneralInsightsGrid } from './general-insights-grid';
import { getConfig } from './grid-config';
import { getGeneralInsightsPlugins } from './plugins';

const appConfig = globalContext.require('component/config/index');
const i18n = globalContext.require('@bingads-webui/i18n-amd-loader!campaignui/feature/competition-tab');
const appState = globalContext.require('component/state/index');
const dateUtil = globalContext.require('component/humanize/date');
const sessionStore = globalContext.requireDefault('component/js-data-storage/session-storage');
const preferenceStoreFactory = globalContext.require('component/bingads-grid/service/filter-preferences').getFilters;
const cacheService = globalContext.require('Services/CacheService');
const dataContextManager = globalContext.require('SummaryDataContextManager');
const edmCampaign = globalContext.requireDefault('component/edm-campaign/index');
const jsdata = globalContext.require('component/js-data-campaign/index');
const instrumentation = globalContext.requireDefault('campaignui/feature/instrumentation/index');
const preferenceServiceLegacy = globalContext.requireDefault('component/preference/service/index');

const permissions = appConfig.get('Permissions');

export function createGeneralInsightsGrid({
  newI18n,
  getAdditionalPlugins,
  preferenceService,
  preferenceConfig,
  router,
  scope,
  context,
  serverContext,
  auctionInsightPerfMarker,
  viewport,
  customHeaderOffset,
  showAlertHandler,
  campaignType,
  selectedCampaignType,
  onCampaignTypeChange,
  refreshChart,
  p$daterange,
  customizedGenerateSchemaForAdGroupProperty,
  customizedGenerateSchemaForCampaignProperty,
  generateSchemaForAccountProperty,
}) {
  instrumentation.addScenario(instrumentationSchema);
  const parentScenario = instrumentation.loadCompetitionTab.create();
  const currentScenario = instrumentation.loadGeneralInsights.create(parentScenario);
  const currentActivity = currentScenario.loadGeneralInsights.create();

  const dataService = new AuctionInsightService({
    scope,
    showAlertHandler,
    i18n,
    currentScenario,
    currentActivity,
    odataPath: appConfig.get('adinsight.odata.url'),
    campaignType: CAMPAIGN_TYPES.Shopping === campaignType ? campaignType : '',
  });

  const campaignTypeForQuery = campaignType === CAMPAIGN_TYPES.Shopping ?
    "Enum.CampaignType'Shopping'" : "Enum.CampaignType'Default'";

  const filterConfig = getFilterConfig({
    scope,
    dataService,
    campaignType,
    featureName: GENERAL_INSIGHTS_FEATURE_NAMES[campaignType],
    existingCampaignFilter: { // todo, some hide logic
      CampaignType: {
        $eq: campaignTypeForQuery,
      },
    },
    appState,
    dateUtil,
    sessionStore,
    preferenceStoreFactory,
    cacheService,
    dataContextManager,
    edmCampaign,
    jsdata,
    i18n,
    permissions,
    customizedGenerateSchemaForAdGroupProperty,
    customizedGenerateSchemaForCampaignProperty,
    generateSchemaForAccountProperty,
    preferenceService,
  });
  const filterService = new FilterService(filterConfig);


  initializeDataSources({
    dataService,
    filterService,
    featureName: GENERAL_INSIGHTS_FEATURE_NAMES[campaignType],
    afterFindAll: ({ rawData, request }) => {
      const { AccountIds } = request;
      const requestLog = _.omit(request, 'AccountIds', 'EntityIds');

      if (AccountIds && AccountIds.length !== 0) {
        _.extend(requestLog, {
          accountAppliedCount: AccountIds.length,
          totalAccountCount: scope.accountsInfo.totalCoun9t,
        });
        currentActivity.trace(`findAll option: ${JSON.stringify(requestLog)}`, 'AuctionInsightService/afterFindAll');

        if (AccountIds.length !== scope.accountsInfo.totalCount) {
          currentActivity.trace(
            `account filter applied. accounts count: ${scope.accountsInfo.totalCount}, accounts filtered: ${AccountIds.length}`,
            'generalInsight/afterFindAll'
          );
        }
      }
      if (rawData.UnderPerformingAccountIds && rawData.UnderPerformingAccountIds.length > 0) {
        currentActivity.trace(`Auction insight light bulb rendered. Request: ${JSON.stringify(requestLog)}`, 'generalInsight/afterFindAll');
      }
    },
  });

  const dataSource = getDataSource(GENERAL_INSIGHTS_DATA_TYPES.Grid);
  const chartDataSource = getDataSource(GENERAL_INSIGHTS_DATA_TYPES.Chart);

  const gridView = new GeneralInsightsGrid({
    filterService,
    dataSource,
    i18n,
    newI18n,
    serverContext,
    instrumentation,
    refreshChart,
    getGridConfig: () => getConfig({
      scope,
      i18n,
      campaignType,
      filterService,
      context,
      dataSource,
      viewport,
      customHeaderOffset,
      currentActivity,
      currentScenario,
    }),
    getPlugins: view => getGeneralInsightsPlugins({
      campaignType,
      selectedCampaignType,
      refreshChart,
      onCampaignTypeChange,
      preferenceService,
      auctionInsightPerfMarker,
      i18n,
      scope,
      getView: () => gridView,
      preferenceConfig,
      preferenceServiceLegacy,
      showAlertHandler,
      p$daterange,
    }).concat(getAdditionalPlugins(view)),
    router,
    scope,
    preferenceConfig,
  });

  // todo rename
  const { grid } = gridView;

  grid.gridView.on('willUpdate', (changes) => {
    if (_.has(changes, 'sortableHeader')) {
      const orderby = _.result(changes.query, 'orderby') || _.result(grid.gridView.get('query'), 'orderby');
      const { sortableHeader } = changes;

      // since the query-build using the old preferenceservice to fetch the sorting information.
      preferenceServiceLegacy.setAtUserLevel({
        Name: `WebApp.CampaignUI.${GENERAL_INSIGHTS_FEATURE_NAMES[campaignType]}`,
        Data: { sortInfo: { orderby, sortableHeader } },
      });
    }
  });

  filterService.on('filter-state-changed', () => {
    grid.gridView.refresh(true);
  });

  return {
    gridView,
    filterConfig,
    filterService,
    currentScenario,
    currentActivity,
    chartDataSource,
    dataService,
  };
}
